import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { Button } from "../components/ui/Button";
import { OperatorModal } from "../components/ui/OperatorModal";
import { ManagerModal } from "../components/ui/ManagerModal";
import Logo from "../components/ui/Logo";
import apiService from "../services/ApiService";
import { ApiError, NetworkError } from "../types/api";

export const WelcomeScreen: React.FC = () => {
  const [showOperatorPrompt, setShowOperatorPrompt] = useState(false);
  const [showManagerPrompt, setShowManagerPrompt] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const navigate = useNavigate();
  const { setOperatorName, setIsManagerAuthenticated } = useAppContext();

  const handleOperatorSubmit = (name: string, shiftId?: string) => {
    setOperatorName(name);
    setShowOperatorPrompt(false);

    // Store shift ID if provided
    if (shiftId) {
      localStorage.setItem("current_shift_id", shiftId);
    }

    navigate(`/multi-camera`);
  };

  const handleManagerSubmit = async (credentials: {
    username?: string;
    password?: string;
    role?: "manager";
  }) => {
    setIsAuthenticating(true);
    setPasswordError("");

    try {
      // Try API authentication first
      const response = await apiService.authenticate({
        ...credentials,
        role: credentials.role || "manager",
      });

      console.log("Manager authenticated successfully:", response);

      setIsManagerAuthenticated(true);
      setShowManagerPrompt(false);
      setPasswordError("");
      navigate("/manager");
    } catch (error) {
      if (error instanceof ApiError) {
        setPasswordError("Invalid credentials. Please try again.");
      } else if (error instanceof NetworkError) {
        // Fallback to hardcoded password when API is unavailable
        console.warn("API unavailable, falling back to local authentication");
        const { MANAGER_PASSWORD } = await import("../constants/auth");

        if (credentials.password === MANAGER_PASSWORD) {
          setIsManagerAuthenticated(true);
          setShowManagerPrompt(false);
          setPasswordError("");
          navigate("/manager");
        } else {
          setPasswordError("Incorrect password");
        }
      } else {
        setPasswordError("An unexpected error occurred.");
        console.error("Manager authentication error:", error);
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleManagerClose = () => {
    setPasswordError("");
    setShowManagerPrompt(false);
    setIsAuthenticating(false);
  };

  return (
    <div className="h-screen w-screen flex flex-col items-center justify-center bg-gray-50 gap-10 p-16">
      <div className="flex flex-row justify-center items-center gap-4">
        <div className="p-4 rounded-xl bg-[#00212fad]">
          <Logo />
        </div>
        <span className="text-5xl font-bold text-center">:</span>
        <h1 className="text-5xl font-bold text-center">BITBOX</h1>
      </div>
      <div className="flex flex-row justify-center items-center gap-8">
        <Button
          variant="secondary"
          size="lg"
          className="px-10 py-5 w-[500px] h-[250px] !text-5xl flex items-center justify-center gap-3 shadow-md hover:shadow-lg transition-all"
          onClick={() => setShowOperatorPrompt(true)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-14 w-14"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          Verificator
        </Button>

        <Button
          variant="secondary"
          size="lg"
          className="px-10 py-5 w-[500px] h-[250px] !text-5xl flex items-center justify-center gap-3 shadow-md hover:shadow-lg transition-all"
          onClick={() => setShowManagerPrompt(true)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-14 w-14"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          Manager
        </Button>
      </div>

      {/* Operator Modal */}
      <OperatorModal
        isOpen={showOperatorPrompt}
        onClose={() => setShowOperatorPrompt(false)}
        onSubmit={handleOperatorSubmit}
      />

      {/* Manager Modal */}
      <ManagerModal
        isOpen={showManagerPrompt}
        onClose={handleManagerClose}
        onSubmit={handleManagerSubmit}
        error={passwordError}
        isLoading={isAuthenticating}
      />
    </div>
  );
};
